import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Card, CardBody, Label } from 'reactstrap';
import { SkeletonTable } from '../../../../../Components/Common/Skeleton';
import notify from '../../../../../utils/notify';
import ParcelField from '../shared/ParcelField';
import ParcelIncotermTableLpg from './ParcelIncotermTableLpg';
import CargoRatioSelector from './CargoRatioSelector';
import {
  getCargoRatioKey,
  getIncotermBasesFromParcel,
  loadCargoRatioTable,
  syncAndLoadCargoRatioTable,
} from './utils';

const ParcelLpgCard = ({
  parcel,
  procurementId,
  quantityUnitLabel,
  requiresCargoRatio = false,
  isReadOnly = false,
  isAlphaEditable = false,
  onCargoRatioStatusChange,
  hasCargoRatioError = false,
  onClearCargoRatioError,
}) => {
  const [selectedRatios, setSelectedRatios] = useState([]);
  const [ratioOptions, setRatioOptions] = useState([]);
  const [tableModel, setTableModel] = useState({
    rows: [],
    hasCargoRatioSelection: false,
  });
  const [isLoadingRows, setIsLoadingRows] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const loadGenerationRef = useRef(0);

  useEffect(() => {
    if (!onCargoRatioStatusChange || !parcel?.id) return;

    if (!requiresCargoRatio) {
      onCargoRatioStatusChange(parcel.id, true);
      return;
    }

    onCargoRatioStatusChange(
      parcel.id,
      Boolean(tableModel.hasCargoRatioSelection)
    );
  }, [
    onCargoRatioStatusChange,
    parcel?.id,
    requiresCargoRatio,
    tableModel.hasCargoRatioSelection,
  ]);

  const incotermBases = useMemo(
    () => getIncotermBasesFromParcel(parcel),
    [parcel]
  );

  const applyLoadedState = useCallback(
    (generation, payload) => {
      if (generation !== loadGenerationRef.current) return;

      setTableModel(payload.tableModel);
      setSelectedRatios(requiresCargoRatio ? payload.confirmedRatios : []);
      setRatioOptions(requiresCargoRatio ? (payload.ratioOptions ?? []) : []);
    },
    [requiresCargoRatio]
  );

  const refreshTable = useCallback(
    async ({ resetBeforeLoad = false, generation } = {}) => {
      const activeGeneration = generation ?? loadGenerationRef.current;

      if (resetBeforeLoad) {
        setSelectedRatios([]);
        setTableModel({ rows: [], hasCargoRatioSelection: false });
      }

      const payload = await loadCargoRatioTable({
        procurementId,
        quantityDeliveryTermId: parcel.id,
        incotermBases,
        unitLabel: quantityUnitLabel,
        skipCargoRatio: !requiresCargoRatio,
      });

      applyLoadedState(activeGeneration, payload);
      return payload;
    },
    [
      applyLoadedState,
      procurementId,
      parcel.id,
      incotermBases,
      quantityUnitLabel,
      requiresCargoRatio,
    ]
  );

  useEffect(() => {
    const generation = ++loadGenerationRef.current;

    const loadExisting = async () => {
      setIsInitialLoading(true);
      try {
        await refreshTable({ resetBeforeLoad: true, generation });
      } finally {
        if (generation === loadGenerationRef.current) {
          setIsInitialLoading(false);
        }
      }
    };

    loadExisting();

    return () => {
      loadGenerationRef.current += 1;
    };
  }, [refreshTable]);

  const handleToggleRatio = ratio => {
    if (isReadOnly) return;
    onClearCargoRatioError?.(parcel.id);
    setSelectedRatios(prev => {
      const ratioKey = getCargoRatioKey(ratio.id);
      const exists = prev.some(item => getCargoRatioKey(item.id) === ratioKey);
      if (exists) {
        return prev.filter(item => getCargoRatioKey(item.id) !== ratioKey);
      }
      return [...prev, ratio];
    });
  };

  const handleSelectCargoRatio = async () => {
    if (isReadOnly) return;
    const generation = loadGenerationRef.current;
    setIsLoadingRows(true);
    try {
      await syncAndLoadCargoRatioTable({
        procurementId,
        quantityDeliveryTermId: parcel.id,
        selectedRatios,
        incotermBases,
        unitLabel: quantityUnitLabel,
      });

      if (generation !== loadGenerationRef.current) return;

      await refreshTable({ generation });
    } catch {
      if (generation === loadGenerationRef.current) {
        notify.error('Failed to save cargo ratio selection. Please try again.');
      }
    } finally {
      if (generation === loadGenerationRef.current) {
        setIsLoadingRows(false);
      }
    }
  };

  const isTableLoading = isInitialLoading || isLoadingRows;

  return (
    <Card className='border shadow-sm mb-3'>
      <CardBody>
        <ParcelField parcel={parcel} />

        {requiresCargoRatio ? (
          <CargoRatioSelector
            id={`cargo-ratio-selection-${parcel.id}`}
            selectedRatios={selectedRatios}
            ratioOptions={ratioOptions}
            onToggleRatio={handleToggleRatio}
            onConfirm={handleSelectCargoRatio}
            isSubmitting={isLoadingRows}
            isReadOnly={isReadOnly}
            hasError={hasCargoRatioError}
          />
        ) : null}

        <div className='mt-3'>
          <Label className='fw-semibold small text-dark mb-2'>Incoterm</Label>
          {isTableLoading ? (
            <SkeletonTable rows={3} columns={8} />
          ) : (
            <ParcelIncotermTableLpg
              tableModel={tableModel}
              requiresCargoRatio={requiresCargoRatio}
              isAlphaEditable={isAlphaEditable}
              procurementId={procurementId || parcel.procurementId}
              quantityDeliveryTermId={parcel.id}
              infoPrompt={parcel.infoPrompt ?? parcel.infoPromptLabel}
              onAlphaSaved={() => {
                const generation = loadGenerationRef.current;
                refreshTable({ generation });
              }}
            />
          )}
        </div>
      </CardBody>
    </Card>
  );
};

const HpsParcelLpg = ({
  parcels = [],
  procurementId,
  quantityUnitLabel = 'MT',
  // parcel => bool; cargo ratio depends on the product type of each cargo
  getRequiresCargoRatio = () => false,
  isReadOnly = false,
  isAlphaEditable = false,
  onCargoRatioStatusChange,
  cargoRatioErrorParcelId = null,
  onClearCargoRatioError,
}) =>
  parcels.map((parcel, index) => (
    <ParcelLpgCard
      key={parcel.id || index}
      parcel={parcel}
      procurementId={procurementId}
      quantityUnitLabel={quantityUnitLabel}
      requiresCargoRatio={getRequiresCargoRatio(parcel)}
      isReadOnly={isReadOnly}
      isAlphaEditable={isAlphaEditable}
      onCargoRatioStatusChange={onCargoRatioStatusChange}
      hasCargoRatioError={cargoRatioErrorParcelId === parcel.id}
      onClearCargoRatioError={onClearCargoRatioError}
    />
  ));

export default HpsParcelLpg;
